import React, { ReactNode } from 'react';
import { Box, Button, PaletteColor, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import { Switch } from '../../atoms';

export type AccordionHeaderProps = {
    subtitle?: string;
    title: ReactNode;
    expandLabel?: ReactNode;
    expandColor?: PaletteColor | string;
    expanded: boolean;
    onToggle: () => void;
    sx?: SxProps;
    titlesSx?: SxProps;
    checkBox?: boolean;
    checked?: boolean;
    checkBoxDisabled?: boolean;
    onChange?: (checked: boolean) => void;
    tooltipDisabled?: string;
    tooltipSelected?: string;
    tooltipSelect?: string;
    // the dark background animation on focus (that grow and pulse)
    disableRipple?: boolean;
};

export const AccordionHeader: React.FunctionComponent<AccordionHeaderProps> = function (props) {
    const { palette } = useTheme();
    const {
        title,
        subtitle,
        expandLabel,
        expandColor = palette.secondary,
        expanded,
        onToggle,
        sx,
        titlesSx,
        checkBox = false,
        checked = false,
        checkBoxDisabled = false,
        onChange,
        tooltipDisabled = 'Element non sélectionnable',
        tooltipSelected = 'Désélectionner',
        tooltipSelect = 'Sélectionner',
        disableRipple = false,
    } = props;

    const headerSx: SxProps = {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 25px 20px 25px',
        textAlign: 'left',
        borderBottomColor: palette.mode === 'dark' ? palette.grey[800] : palette.grey[200],
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        ...sx,
    };

    const expandSx: SxProps = {
        color: typeof expandColor === 'string' ? expandColor : expandColor.main,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '0 12px',
    };
    const infoSx: SxProps = {
        display: 'flex',
        alignItems: 'center',
        gap: '0 12px',
        flex: 1,
        minWidth: 0,
    };

    const iconSx: SxProps = {
        lineHeight: 0,
        fontSize: '16px',
        fontWeight: '400',
        display: 'flex',
        alignItems: 'center',
    };

    const subtitlesSx: SxProps = {
        width: '100%',
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '15.6px',
        marginTop: '5px',
        color: 'text.gris50',
    };

    const { enqueueSnackbar } = useSnackbar();

    const getCheckBoxTooltipTitle = (): string => {
        if (checkBoxDisabled) {
            return tooltipDisabled;
        }
        return checked ? tooltipSelected : tooltipSelect;
    };

    return (
        <Button
            role="button"
            data-testid="accordion-header"
            sx={headerSx}
            onClick={onToggle}
            color="secondary"
            disableRipple={disableRipple}
        >
            <Box sx={infoSx}>
                {checkBox && onChange ? (
                    <Tooltip title={getCheckBoxTooltipTitle()}>
                        <Box
                            onClick={(e) => {
                                if (checkBoxDisabled) {
                                    enqueueSnackbar(getCheckBoxTooltipTitle(), {
                                        variant: 'error',
                                        autoHideDuration: 2000,
                                    });
                                }
                                e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                            }}
                        >
                            <Switch
                                size="small"
                                aria-label="switch"
                                disabled={checkBoxDisabled}
                                checked={checked}
                                onClick={(e) => {
                                    e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                }}
                                onChange={(value, state) => {
                                    onChange(state);
                                }}
                            />
                        </Box>
                    </Tooltip>
                ) : null}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        color={palette.secondary.main}
                        sx={titlesSx}
                    >
                        {title}
                    </Typography>
                    {subtitle ? <Typography sx={subtitlesSx}>{subtitle}</Typography> : null}
                </Box>
            </Box>
            <Box sx={expandSx}>
                {expandLabel}
                <FaOptionIcon
                    sx={iconSx}
                    icon={['fas', expanded ? 'chevron-up' : 'chevron-down']}
                    size="xs"
                />
            </Box>
        </Button>
    );
};
